import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import { PHONE_DISPLAY, WA_LINK } from "../data/site";

const NAV = [
  { key: "services", path: "/servicios" },
  { key: "packs", path: "/packs" },
  { key: "works", path: "/trabajos-realizados" },
  { key: "about", path: "/sobre-ecoglow" },
  { key: "blog", path: "/blog" },
  { key: "contact", path: "/contacto" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const prefix = lang === "ca" ? "/ca" : "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-12">
        <Link
          to={prefix || "/"}
          data-testid="header-logo-link"
          className="flex items-center gap-3"
          onClick={() => setOpen(false)}
        >
          <img src="/logo.jpg" alt="EcoGlow Detailing" className="h-10 w-auto rounded-sm object-contain" />
          <span className="hidden font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 sm:block">
            Detailing Studio
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Principal">
          {NAV.map((n) => (
            <Link
              key={n.key}
              to={`${prefix}${n.path}`}
              data-testid={`nav-${n.key}-link`}
              className="text-sm font-medium text-zinc-300 transition-colors duration-300 hover:text-[#8BFF32]"
            >
              {t(`nav.${n.key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/10 p-0.5" data-testid="language-switcher">
            {["es", "ca"].map((l) => (
              <button
                key={l}
                data-testid={`language-switch-${l}-button`}
                onClick={() => setLang(l)}
                className={`rounded-full px-2.5 py-1 font-mono2 text-[11px] font-bold uppercase tracking-wider transition-colors duration-300 ${
                  lang === l ? "bg-[#63E51D] text-black" : "text-zinc-400 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="hidden items-center gap-2 rounded-full bg-[#63E51D] px-4 py-2 text-sm font-bold text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_20px_rgba(99,229,29,0.4)] sm:flex"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="rounded-full border border-white/10 p-2 text-white lg:hidden"
            aria-label="Menú"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/5 bg-[#050505]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {[{ key: "home", path: "/" }, ...NAV].map((n) => (
                <Link
                  key={n.key}
                  to={n.path === "/" ? prefix || "/" : `${prefix}${n.path}`}
                  data-testid={`mobile-nav-${n.key}-link`}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl font-bold uppercase tracking-wide text-white transition-colors hover:text-[#8BFF32]"
                >
                  {t(`nav.${n.key}`)}
                </Link>
              ))}
              <div className="mt-4 flex gap-3">
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-button"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#63E51D] px-4 py-3 text-sm font-bold text-black"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a
                  href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
                  data-testid="mobile-call-button"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-3 text-sm font-bold text-white"
                >
                  <Phone className="h-4 w-4" /> {t("contact.call")}
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
